import { apiFetch } from './api';
import { buildDocHtml } from './docHtml';
import { safeFileName } from './safeFileName';

/**
 * Vygeneruje PDF přes backend (Playwright) a stáhne ho. Bez záhlaví/zápatí
 * prohlížeče (datum/čas/URL).
 *
 * Posílá POUZE kořen dokumentu (`[data-doc-page]`, tj. komponentu DocPage) —
 * ne celou aplikaci s menu/hlavičkou — a s CSS vloženým inline (viz
 * `buildDocHtml`). Tím je výstup stylovaný i v produkci, kde je CSS externí
 * soubor, na který backend (renderuje proti `http://pdf.local/`) nedosáhne.
 *
 * Volitelně lze předat vlastní `html` (např. už sestavené přes `buildDocHtml`
 * z konkrétního previewRef) — má přednost před automatickým hledáním.
 */
export async function printToPdf(filename: string, html?: string): Promise<void> {
  const { blob, safeName } = (await generatePdfBlob(filename, html)) ?? {};
  if (!blob || !safeName) return;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Jako `printToPdf`, ale výsledné PDF NEstahuje — zobrazí ho inline v aktuální
 * záložce (`window.location`). Používá se pro „náhled" z archivu, kde se
 * dokument otevře v nové záložce s `?pdf=preview` a po vygenerování se sám
 * přepne na zobrazení PDF. Blob URL se zde záměrně neuvolňuje — prohlížeč ho
 * potřebuje pro vykreslení.
 */
export async function previewPdf(filename: string, html?: string): Promise<void> {
  const { blob } = (await generatePdfBlob(filename, html)) ?? {};
  if (!blob) return;
  window.location.href = URL.createObjectURL(blob);
}

/** Sestaví HTML (pokud není předané) a nechá backend vygenerovat PDF blob. */
async function generatePdfBlob(
  filename: string,
  html?: string,
): Promise<{ blob: Blob; safeName: string } | null> {
  if (!navigator.onLine) {
    alert('Generování PDF vyžaduje připojení k internetu.');
    return null;
  }

  const finalHtml = html ?? (await buildPrintHtml());
  if (!finalHtml) {
    alert('Nepodařilo se najít obsah dokumentu k tisku.');
    return null;
  }
  // Názvy z formulářů obsahují číslo dokumentu (např. „2026/001") nebo název
  // stavby s diakritikou — lomítko by rozbilo download a diakritika hlavičku
  // Content-Disposition na backendu.
  const safeName = safeFileName(filename);
  // Dokument si sám řekne o tisk na šířku značkou `data-doc-landscape` uvnitř
  // `[data-doc-page]` (registr rizik — široká tabulka). Detekce z HTML, aby
  // fungovala i pro náhled/stažení z archivu (`?pdf=preview`), kde se
  // `printToPdf` volá bez znalosti konkrétního formuláře.
  const landscape = /data-doc-landscape/.test(finalHtml);

  try {
    const blob = (await apiFetch('/pdf/generate', {
      method: 'POST',
      body: { html: finalHtml, filename: safeName, landscape },
    })) as Blob;
    return { blob, safeName };
  } catch (err) {
    console.error('generatePdfBlob failed', err);
    alert('Nepodařilo se vygenerovat PDF.');
    return null;
  }
}

/** Najde kořen dokumentu na stránce a sestaví self-contained HTML s inline CSS. */
function buildPrintHtml(): Promise<string> {
  const docRoot = document.querySelector<HTMLElement>('[data-doc-page]');
  return buildDocHtml(docRoot);
}
